import React, { memo, Fragment, useState, useContext, useEffect, useRef } from 'react'
import Joyride, { STATUS } from 'react-joyride'
import { AuthContext } from '@/context/auth-context'
import { GuideContext } from '@/context/guide-context'
import { gql, useQuery } from '@apollo/client'
import { USER_FIELDS } from '@/graphql/user-fragments'
import { Row, Col } from 'react-bootstrap'
import UserProfile from '@/components/users/UserProfile'
import { showNotification } from '@/utils/notifications'
import { ToastContainer } from 'react-toastify'
import { CubeLoader } from '@/components/ui/cube-loader'
import { useOnboardingTour } from '@/utils/onboarding-tour-hook'

const GET_USER = gql`
    ${USER_FIELDS}
    query Users($where: UserWhere) {
        users(where: $where) {
            campaignsAggregate {
                count
            }
            ...UserFields
        }
    }
`

const AdminProfile = memo(() => {
    const { user } = useContext(AuthContext)
    const { guide } = useContext(GuideContext)
    const { focus, handleFocusTour } = useOnboardingTour()
    const [guidance, setGuidance] = useState(false)
    const [userData, setUserData] = useState([])
    const { data, loading } = useQuery(GET_USER, {
        variables: { where: { id: user.id } },
    })

    const guidedRef = useRef(false)

    const [run, setRun] = useState(false)
    const [steps] = useState([
        {
            target: '.step-1',
            content: 'Enter your phone number',
        },
        {
            target: '.step-2',
            content: 'Click this button to save your details',
        },
    ])

    const handleTourCallback = (data) => {
        const { status } = data
        handleFocusTour(data, steps)
        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            setRun(false)
        }
    }

    const updateUserData = (userData) => {
        setUserData(userData)
    }

    const userDetailsCompleted = () => {
        setGuidance(false)
    }

    // If this is a fresh user, show the tour.
    useEffect(() => {
        if (data?.users[0] && guide === 'on') {
            if (!data.users[0].address || !data.users[0].phone) {
                setGuidance(true)
                if (!guidedRef.current) {
                    showNotification(
                        <div>
                            <p>Welcome to the Turba Media onboarding guide!</p>
                            <p>
                                You can turn the tour on or off at any time by clicking the round button with the "eye"
                                icon in the top-right panel.
                            </p>
                            <p>
                                Please enter your personal details below. You can follow the blue dot from field to
                                field to complete the form.
                            </p>
                            <p>Note: Your phone number, business name and business address are required.</p>
                        </div>,
                        'info',
                        false,
                        'Continue',
                        () => focus('.step-1'),
                        false,
                    )
                    guidedRef.current = true
                }
            }
        }
    }, [data, guide, guidedRef])

    useEffect(() => {
        if (!loading && data && data.users && data.users.length > 0) {
            const checkElements = setInterval(() => {
                const allTargetsMounted = steps.every((step) => document.querySelector(step.target))
                if (allTargetsMounted) {
                    clearInterval(checkElements)
                    setRun(true)
                }
            }, 100)
        }
    }, [loading, data, steps])

    return (
        <Fragment>
            <Joyride
                steps={steps}
                run={guidance && guide === 'on'}
                continuous={false}
                showProgress={false}
                showSkipButton={false}
                callback={handleTourCallback}
                styles={{ options: { primaryColor: '#437eeb' } }}
            />
            <ToastContainer />
            {loading ? (
                <CubeLoader size="default" fullScreen={true} />
            ) : (
                <Row>
                    <Col lg="12">
                        <UserProfile
                            updateUserData={updateUserData}
                            data={data}
                            user={user}
                            callback={userDetailsCompleted}
                        />
                    </Col>
                </Row>
            )}
        </Fragment>
    )
})

AdminProfile.displayName = 'AdminProfile'
export default AdminProfile
